import { Alert, Box, Snackbar, TextField, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ANALYTICS_EVENTS } from "../../../../../../analytics-store";
import BriaButton from "../../../../../../components/common/BriaButton/BriaButton";
import BriaIconButton from "../../../../../../components/common/BriaIconButton/BriaIconButton";
import ConfirmationPopup from "../../../../../../components/common/ConfirmationPopup/ConfirmationPopup";
import ExportButton from "../../../../../../components/common/ExportButton/ExportButton";
import IframeCloseButton from "../../../../../../components/common/IframeCloseButton/IframeCloseButton";
import RouterConstants from "../../../../../../constants/RouterConstants";
import { getSelectedOrganization } from "../../../../../../helpers/localStorage";
import useSecureNavigate from "../../../../../../hooks/useSecureNavigate";
import { useAppStore } from "../../../../../../hooks/useStores";
import InputLayout from "../../../../../../layout/InputLayout/InputLayout";
import iframeStore from "../../../../../../pages/IframeNew/iframe-store";
import { isAdsEditor } from "../../../../../../utils";
import useCampaignHeaderUtils from "../../../../../CustomUtils/CampaignHeaderUtils";
import useScenesUtils from "../../../../../CustomUtils/ScenesUtils";
import useDesignEditorUtils from "../../../../../CustomUtils/UseDesignEditor";
import useTemplateUtils from "../../../../../CustomUtils/UseTemplateUtils";
import Icons from "../../../../../components/Icons";
import CampaignJsonImportButton from "../../../../../components/common/CampaignJsonImportButton/CampaignJsonImportButton";
import { DesignEditorContext } from "../../../../../contexts/DesignEditor";
import useDesignEditorContext from "../../../../../hooks/useDesignEditorContext";
import { useEditor } from "../../../../../hooks/useEditor";
import { SecondaryTabTypeEnum } from "../../Panels/panelItems";
import styles from "./AdsEditorHeader.module.scss";

const AdsEditorHeader = () => {
	const { uiStore, imagesStore, campaignStore, designEditorStore, analyticsStore } = useAppStore();
	const editor = useEditor();
	const { setScenes, scenes } = useContext(DesignEditorContext);
	const { updateScenes } = useScenesUtils();
	const { setCurrentScene } = useDesignEditorContext();
	const [isUndoing, setIsUndoing] = useState(false);
	const [isRedoing, setIsRedoing] = useState(false);
	const { t } = useTranslation("translation", { keyPrefix: "editor.header" });
	const [isCampaignOrTemplateCreating, setIsCampaignOrTemplateCreating] = useState<boolean>(false);
	const [isCampaignOrTemplateUpdating, setIsCampaignOrTemplateUpdating] = useState<boolean>(false);
	const [isValidTemplateName, setIsValidTemplateName] = useState<boolean>(true);
	const { t: templateTranslation } = useTranslation("translation", { keyPrefix: "editor.popup.template" });
	const { t: messagesTranslation } = useTranslation("translation", { keyPrefix: "editor.popup.messages" });
	const { saveTemplate, saveNewTemplate } = useCampaignHeaderUtils();
	const navigate = useSecureNavigate();
	const { makeDownloadTemplate } = useDesignEditorUtils();
	const { handleDownloadTemplate, handleIframeSaveTemplate, loadTemplate } = useTemplateUtils();
	const [isTemplateDownloading, setIsTemplateDownloading] = useState<boolean>(false);
	const [isTemplateImporting, setIsTemplateImporting] = useState<boolean>(false);
	const [openGenerateAdsConfirmationPopup, setOpenGenerateAdsConfirmationPopup] = useState<boolean>(false);
	const [isSnackbarOpen, setSnackbarOpen] = useState(false);

	const undoButtonDisabled =
		!editor?.history?.getUndos?.()?.length ||
		editor.history.getUndos().length <= 1 ||
		imagesStore.secondaryTabType === SecondaryTabTypeEnum.SMART_IMAGE;

	const redoButtonDsiabled =
		!editor?.history?.getRedos?.()?.length ||
		editor.history.getRedos().length == 0 ||
		imagesStore.secondaryTabType === SecondaryTabTypeEnum.SMART_IMAGE;
	editor?.history?.setScenesFunc(setScenes);
	editor?.history.getUpdatedScenes(updateScenes);
	editor?.history.setCurrentScenesFunc(setCurrentScene);

	useEffect(() => {
		resetSceneToDeleteProperty();
	}, [editor, editor?.history?.getRedos?.()?.length, editor?.history.getUndos().length]);

	const resetSceneToDeleteProperty = () => {
		const scn = scenes.find((scene) => scene.id === campaignStore.sceneToDelete?.id);
		if (scn) {
			campaignStore.sceneToDelete = undefined;
		}
	};

	const undo = () => {
		if (isUndoing) return;
		setIsUndoing(true);
		editor?.history.undo();
		setTimeout(() => {
			setIsUndoing(false);
			resetSceneToDeleteProperty();
		}, 350);
	};

	const redo = () => {
		if (isRedoing) return;
		setIsRedoing(true);
		editor?.history.redo();
		setTimeout(() => {
			setIsRedoing(false);
			resetSceneToDeleteProperty();
		}, 350);
	};
	const handleNewTemplateClick = () => {
		campaignStore.setProperty("templateName", "");
		campaignStore.setProperty("openDuplicateCampaignOrTemplateModal", true);
	};

	const handleSaveTemplate = async () => {
		setIsCampaignOrTemplateUpdating(true);
		await saveTemplate();
		setIsCampaignOrTemplateUpdating(false);
		setSnackbarOpen(true);
	};

	const onJsonExport = async () => {
		let templateName;
		if (campaignStore.selectedTemplate) {
			templateName = `${"Template"}_${campaignStore.selectedTemplate.name}`;
		}
		await makeDownloadTemplate(templateName);
	};
	const onDownload = async () => {
		if (campaignStore.selectedTemplate) {
			setIsTemplateDownloading(true);
			await handleDownloadTemplate(campaignStore.selectedTemplate);
			setIsTemplateDownloading(false);
			analyticsStore.logEvent(ANALYTICS_EVENTS.EXPORT_CAMPAIGN);
		}
	};

	const onIframeSave = async () => {
		if (campaignStore.selectedTemplate) {
			setIsTemplateDownloading(true);
			await handleIframeSaveTemplate(campaignStore.selectedTemplate);
			setIsTemplateDownloading(false);
		}
	};

	const handleImportTemplate = async (template: any) => {
		setIsTemplateImporting(true);
		await loadTemplate(template);
		setIsTemplateImporting(false);
	};

	const handleOpenGenerateAdsConfirmationPopup = async () => {
		setOpenGenerateAdsConfirmationPopup(true);
	};
	const handleCloseSnackbar = () => {
		setSnackbarOpen(false);
	};
	return (
		<Box
			className={clsx({
				[styles.adsEditorHeader]: true,
				[styles.noMarginRight]: uiStore.showGuideSection,
			})}
		>
			<Box className={styles.leftSideButtons}>
				{/* To add it later */}
				{/* {!isBrandTemplateEditor && (
					<BriaButton
						buttonType="textMedium"
						onClick={() => {
							campaignStore.selectedTemplate = null;
						}}
						className={styles.btn}
					>
						{t("startOver")}
					</BriaButton>
				)} */}

				{!iframeStore.isIframe() && !isAdsEditor() && (
					<CampaignJsonImportButton onImport={handleImportTemplate} isLoading={isTemplateImporting} />
				)}
				<BriaIconButton
					className={clsx(styles.customIconButton, {
						[styles.disabled]: undoButtonDisabled,
					})}
					disabled={undoButtonDisabled}
					title={t("undo")}
					buttonType="secondaryLarge"
					onClick={undo}
				>
					<Icons.Undo size={24} />
				</BriaIconButton>
				<BriaIconButton
					className={clsx(styles.customIconButton, {
						[styles.disabled]: redoButtonDsiabled,
					})}
					disabled={redoButtonDsiabled}
					title={t("redo")}
					buttonType="secondaryLarge"
					onClick={redo}
				>
					<Icons.Redo size={24} />
				</BriaIconButton>
				{!isAdsEditor() && (
					<>
						<BriaButton
							onClick={async () => {
								await handleSaveTemplate();
							}}
							buttonType="textMedium"
							className={styles.btn}
							loading={isCampaignOrTemplateUpdating}
							disabled={
								campaignStore.isTemplateUpdating ||
								isCampaignOrTemplateUpdating ||
								campaignStore.isCreatingTemplate ||
								!campaignStore.selectedTemplate ||
								!!(
									campaignStore.selectedTemplate &&
									campaignStore.selectedTemplate?.org_id !==
										getSelectedOrganization()?.organization.uid
								)
							}
						>
							{t("saveTemplate")}
						</BriaButton>

						<BriaButton
							buttonType="textMedium"
							onClick={() => {
								handleNewTemplateClick();
							}}
							className={styles.btn}
							disabled={!campaignStore.selectedTemplate?.id}
						>
							{t("saveNewTemplate")}
						</BriaButton>
					</>
				)}
			</Box>

			<Box className={styles.RightSideButtons}>
				{/* {isBrandTemplateEditor && (
					<BriaButton
						buttonType="textMedium"
						onClick={() => {
							handleOpenGuide(0);
						}}
						className={styles.guidebtn}
					>
						{templateGuideTranslation("guide")}
					</BriaButton>
				)} */}
				{isAdsEditor() && (
					<BriaButton
						buttonType="textMedium"
						onClick={() => {
							handleOpenGenerateAdsConfirmationPopup();
						}}
						className={styles.btn}
					>
						{t("backToAdsGeneration")}
					</BriaButton>
				)}

				{isAdsEditor() && (
					<ExportButton
						disableExportPopup={true}
						exportDropDownClassName={styles.exportButtonDropDown}
						exportJson={false}
						onDownload={onDownload}
						onIframeSave={onIframeSave}
						onJsonExport={onJsonExport}
						loading={isTemplateDownloading}
						pngLabel={t("pngLabel")}
						jsonLabel={t("jsonLabel")}
						disabled={
							!campaignStore.selectedTemplate ||
							designEditorStore.isLoadingTemplate ||
							isTemplateDownloading
						}
					/>
				)}

				<IframeCloseButton />
			</Box>
			<ConfirmationPopup
				onClose={() => {
					campaignStore.setProperty("openDuplicateCampaignOrTemplateModal", false);
				}}
				paperClassName={clsx(styles.campaignConfirmationPopup)}
				title={templateTranslation("title1")}
				confirmButtonText={templateTranslation("saveNewTemplate")}
				loading={isCampaignOrTemplateCreating}
				disableConfirm={isCampaignOrTemplateCreating || campaignStore.templateName.trim() === ""}
				open={campaignStore.openDuplicateCampaignOrTemplateModal}
				hideCancel={true}
				onClick={() => {
					setIsCampaignOrTemplateCreating(true);
					const templateName = campaignStore.templateName;
					const saveAction = saveNewTemplate;
					saveAction({ templateName, setIsValidTemplateName }).then(() => {
						setIsCampaignOrTemplateCreating(false);
						setSnackbarOpen(true);
					});
				}}
			>
				<Box>
					<InputLayout
						showLabel={false}
						validateOn={!isValidTemplateName}
						validationMessage={templateTranslation("templateValidationMessage")}
					>
						<TextField
							value={campaignStore.templateName}
							onChange={(e) => {
								campaignStore.setProperty("templateName", e.target.value);
							}}
							placeholder={templateTranslation("input")}
							fullWidth
							InputProps={{ classes: { root: styles.textFieldDataSetCreation } }}
						/>
					</InputLayout>
				</Box>
			</ConfirmationPopup>

			<ConfirmationPopup
				onClose={() => {
					setOpenGenerateAdsConfirmationPopup(false);
				}}
				maxWidth="616px"
				paperClassName={styles.popupPaper}
				title={t("OpenGenerateAdsConfirmationPopup.title")}
				description={
					<Typography className={styles.description}>
						{t("OpenGenerateAdsConfirmationPopup.description")}
					</Typography>
				}
				subDescription={
					<Typography className={styles.subDesc}>
						{t("OpenGenerateAdsConfirmationPopup.subDescription")}
					</Typography>
				}
				confirmButtonText={t("OpenGenerateAdsConfirmationPopup.buttonText")}
				open={openGenerateAdsConfirmationPopup}
				onClick={() => {
					navigate(RouterConstants.ADS_GENERATION.DEMO.ADS_GENERATION.fullPath);
				}}
				onSecondaryClick={() => {
					setOpenGenerateAdsConfirmationPopup(false);
				}}
			/>
			<Snackbar
				open={isSnackbarOpen}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
				className={styles.Snackbar}
			>
				<Alert onClose={handleCloseSnackbar} severity="success">
					{messagesTranslation("saveTemplae")}
				</Alert>
			</Snackbar>
		</Box>
	);
};
export default observer(AdsEditorHeader);
